import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import kz_logo from "../../../assets/images/kz_logo.png";
import gdpr_badge from "../../../assets/images/gdpr-badge.png";
import Input from "../Elements/Input";
import Button from "../Elements/Button";
import DropDown from "../Elements/DropDown";
import TermsAndPolicyModal from "./TermsAndPolicyModal";
import { openPrivacyPage } from "../../utils/privacyPage";
import TermsAndUseContent from "../../utils/TermsAndUseContent";
import "./SessionTimeoutModal.css";
import "../Authentication/authenticationStyles.css";

const EVENT_NAME = "app:session-expired";
const LOCK_ATTR = "data-session-lock";
const LOCK_ID = "__session-lock-overlay__";
const BODY_CLASS = "session-timeout-active";

const SessionTimeoutModal = () => {
  const [isActive, setIsActive] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [termsModalStatus, setTermsModalStatus] = useState(false);

  const lockOverlayRef = useRef(null);
  const bodyObserverRef = useRef(null);
  const hiddenModalsRef = useRef([]);
  const portalRef = useRef(null);

  useEffect(() => {
    const el = document.createElement("div");
    el.setAttribute("id", "__session-timeout-portal__");
    document.body.appendChild(el);
    portalRef.current = el;
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, []);
  const buildLockDiv = () => {
    const div = document.createElement("div");
    div.id = LOCK_ID;
    div.setAttribute(LOCK_ATTR, "true");
    Object.assign(div.style, {
      position: "fixed",
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      zIndex: "9998",
      pointerEvents: "all",
      background: "transparent",
    });
    return div;
  };

  const activateLock = useCallback(() => {
    hiddenModalsRef.current = [];
    document.querySelectorAll(".modal.show").forEach((modal) => {
      hiddenModalsRef.current.push(modal);
      modal.style.display = "none";
    });
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    document.body.classList.add(BODY_CLASS);
    const lock = buildLockDiv();
    lockOverlayRef.current = lock;
    document.body.appendChild(lock);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          for (const removed of mutation.removedNodes) {
            if (
              removed.nodeType === Node.ELEMENT_NODE &&
              removed.getAttribute?.(LOCK_ATTR) === "true"
            ) {
              const newLock = buildLockDiv();
              lockOverlayRef.current = newLock;
              document.body.appendChild(newLock);
            }
          }
        }
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class" &&
          !document.body.classList.contains(BODY_CLASS)
        ) {
          document.body.classList.add(BODY_CLASS);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    bodyObserverRef.current = observer;
  }, []);

  const deactivateLock = useCallback(() => {
    if (bodyObserverRef.current) {
      bodyObserverRef.current.disconnect();
      bodyObserverRef.current = null;
    }
    const existing = document.getElementById(LOCK_ID);
    if (existing) existing.remove();
    lockOverlayRef.current = null;
    document.body.classList.remove(BODY_CLASS);
    hiddenModalsRef.current.forEach((modal) => {
      if (modal.isConnected) modal.style.removeProperty("display");
    });
    hiddenModalsRef.current = [];
  }, []);

  useEffect(() => {
    if (!isActive) return;
    const tid = setTimeout(() => {
      portalRef.current
        ?.querySelector('input[type="email"]')
        ?.focus();
    }, 80);
    return () => clearTimeout(tid);
  }, [isActive]);

  useEffect(() => {
    const onExpired = () => {
      setIsActive(true);
      setEmail("");
      setPassword("");
      setLoginError("");
      activateLock();
    };

    window.addEventListener(EVENT_NAME, onExpired);
    return () => window.removeEventListener(EVENT_NAME, onExpired);
  }, [activateLock]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLoginError("");

    try {
      const loginPageRes = await fetch("/users/login", {
        method: "GET",
        credentials: "include",
      });
      const loginPageHtml = await loginPageRes.text();
      const parser = new DOMParser();
      const loginDoc = parser.parseFromString(loginPageHtml, "text/html");

      const freshToken =
        loginDoc.querySelector('input[name="authenticity_token"]')?.value ||
        loginDoc
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content");

      if (freshToken) {
        const existingMeta = document.querySelector('meta[name="csrf-token"]');
        if (existingMeta) existingMeta.setAttribute("content", freshToken);
      }

      const body = new URLSearchParams();
      body.append("user[email]", email);
      body.append("user[password]", password);
      body.append("user[remember_me]", rememberMe ? "1" : "0");
      body.append("authenticity_token", freshToken || "");

      const response = await fetch("/users/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "text/html",
        },
        body: body.toString(),
        credentials: "include",
        redirect: "follow",
      });

      const finalPath = new URL(response.url).pathname;
      const loginSucceeded = !finalPath.includes("/login");

      if (loginSucceeded) {
        window.__SESSION_TIMEOUT_ACTIVE__ = false;
        window.location.reload();
        return;
      } else {
        setLoginError("Invalid email or password. Please try again.");
      }
    } catch {
      setLoginError("Login failed. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isActive || !portalRef.current) return null;

  return ReactDOM.createPortal(
    <div
      className="session-timeout-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Session expired – please log in"
      onKeyDown={(e) => e.key === "Escape" && e.stopPropagation()}
    >
      <div className="session-timeout-card">
        <img src={kz_logo} alt="Kinetizine" className="session-timeout-logo" />

        <div className="session-expired-banner">
          <div className="session-expired-text">
            <strong>Your session has expired</strong>
            Please log in again
            <br />
            to continue where you left off.
          </div>
        </div>

        <form onSubmit={handleSubmit} className="session-timeout-form" noValidate>
          {loginError && (
            <div className="alert-devise session-timeout-error">{loginError}</div>
          )}

          <Input
            type="email"
            placeholder="Email"
            name="user[email]"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Input
            type="password"
            placeholder="Password"
            name="user[password]"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Input
            classList="remember-me-wrapper p-0 text-start mt-4"
            className="me-1 login-check form-check-input"
            type="checkbox"
            name="user[remember_me]"
            id="sessionRememberMe"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          >
            <span className="d-flex justify-content-between align-items-center w-100">
              <label className="mb-0 small" htmlFor="sessionRememberMe">Remember Me</label>
              <img
                src={gdpr_badge}
                alt="GDPR Badge"
                height="25"
                onClick={openPrivacyPage}
                style={{ cursor: "pointer" }}
              />
            </span>
          </Input>

          <Button
            classList="login-btn"
            type="submit"
            text={isSubmitting ? "Logging in…" : "Log In"}
            disabled={isSubmitting}
          />
        </form>

        <div className="signup-action-wrapper">
          <p>
            Don't have an account?{" "}
            <a href="/signup" className="text-underline">
              Sign Up
            </a>
          </p>
          <p className="text-underline">
            <a href="/users/password/new">Forgot password?</a>
          </p>
        </div>

        <div className="terms-wrapper">
          <p>
            <a
              href="javascript:void(0)"
              onClick={() => setTermsModalStatus(true)}
            >
              Terms of Service
            </a>
          </p>
          <DropDown
            title={<span>options</span>}
            classList="d-flex align-self-end"
            customClassList="text-dropdown"
            optionList={[
              { url: "/users/confirmation/new", title: "Resend Activation Link" },
              { url: "/users/unlock/new", title: "Unlock Account" },
              { url: "/users/password/new", title: "Forgot password?" },
            ]}
          />
        </div>
      </div>

      <TermsAndPolicyModal
        modalStatus={termsModalStatus}
        modalActiveStatus={setTermsModalStatus}
        title="Terms of Use and Service"
      >
        <TermsAndUseContent />
      </TermsAndPolicyModal>
    </div>,
    portalRef.current
  );
};

export default SessionTimeoutModal;
